/**
 * The workspace **Collaborators** tab (spec M1-B1 §5.1, §7.1).
 *
 * Round 3 rendered `User 1000000000003` and offered nothing: `workspaces.addMembers` /
 * `.removeMembers` existed and were typed, but nothing called them. This is the basic
 * surface — who is in the workspace, with their role, add by search, remove — and it is
 * deliberately not the full permissions screen, which is M8.P1 (the **Permissions** tab next
 * to this one is still disabled, greyed in the reference too).
 *
 * Membership decides who can reach every board in the workspace (F8 §6), so a removal is
 * confirmed and the list is replaced with the mutation's own answer rather than refetched.
 */
import { useConfirm, useToast } from '@elizmonday/ui';
import { PersonAvatar } from '../board-view/columns/primitives';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTRPC } from '../../lib/trpc';
import { useWorkspaceMembers, useWorkspaceMutations } from './workspaces';
import './boards.css';

interface AccountUser {
  id: string;
  name: string;
  email: string;
}

/** The picker has no colour to show yet; the roster does, from the server. */
const PICKER_AVATAR_COLOR = 'american-gray';

export function WorkspaceMembers({ workspaceId }: { workspaceId: string }) {
  const trpc = useTRPC();
  const members = useWorkspaceMembers(workspaceId);
  const mutations = useWorkspaceMutations();
  const confirm = useConfirm();
  const toast = useToast();

  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  /*
   * The picker searches the **account**, not the workspace: adding someone who is already a
   * member is the one thing it must not offer, and `account.users.list` is the only list that
   * has everybody. It only queries once there is something to search for, so opening the tab
   * costs one request, not two.
   */
  const candidates = useQuery({
    ...trpc.account.users.list.queryOptions({ search: query.trim(), limit: 10 }),
    enabled: query.trim().length > 0,
  });

  const rows = members.data ?? [];
  const existing = new Set(rows.map((member) => member.userId).filter(Boolean));
  const offered = ((candidates.data?.rows ?? []) as AccountUser[]).filter(
    (user) => !existing.has(user.id),
  );

  const add = async (user: AccountUser) => {
    setBusy(true);
    try {
      await mutations.addMembers.mutateAsync({ id: workspaceId, userIds: [user.id] });
      await members.refetch();
      setQuery('');
      toast(`${user.name} added`, { kind: 'positive' });
    } catch (error) {
      toast(error instanceof Error ? error.message : 'They could not be added', { kind: 'negative' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member: { userId: string | null; name: string | null }) => {
    if (!member.userId) return;
    const who = member.name ?? 'This person';
    const ok = await confirm({
      title: `Remove ${who}?`,
      body: 'They lose access to every board in this workspace that they can only reach through it.',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await mutations.removeMembers.mutateAsync({ id: workspaceId, userIds: [member.userId] });
      await members.refetch();
      toast(`${who} removed`, { kind: 'positive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ez-members" data-testid="workspace-collaborators">
      <div className="ez-members__add">
        <input
          className="ez-members__search"
          data-testid="member-search"
          placeholder="Add people by name or email"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {query.trim() !== '' ? (
          <ul className="ez-members__suggestions" data-testid="member-suggestions">
            {offered.map((user) => (
              <li key={user.id}>
                <button
                  type="button"
                  disabled={busy}
                  data-testid={`member-add-${user.id}`}
                  onClick={() => void add(user)}
                >
                  <PersonAvatar name={user.name} color={PICKER_AVATAR_COLOR} size={20} />
                  <span>{user.name}</span>
                  <span className="ez-members__email">{user.email}</span>
                </button>
              </li>
            ))}
            {offered.length === 0 ? (
              <li className="ez-members__none">
                {candidates.isPending ? 'Searching…' : 'Nobody new matches'}
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>

      <ul className="ez-members__list" data-testid="member-list">
        {rows.map((member) => (
          <li key={member.id} data-testid={`member-${member.userId ?? member.teamId ?? member.id}`}>
            <PersonAvatar
              name={member.name ?? '?'}
              color={member.color ?? PICKER_AVATAR_COLOR}
              photoUrl={member.photoUrl}
              size={24}
            />
            <span className="ez-members__name">
              {member.name ?? (member.teamId ? `Team ${member.teamId}` : 'Unknown')}
            </span>
            <span className="ez-members__email">{member.email ?? ''}</span>
            <span className="ez-members__role">{member.role}</span>
            {member.userId ? (
              <button
                type="button"
                className="ez-members__remove"
                disabled={busy}
                data-testid={`member-remove-${member.userId}`}
                onClick={() => void remove(member)}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
        {rows.length === 0 ? <li className="ez-members__none">Only you</li> : null}
      </ul>
    </div>
  );
}
